import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function CornwallCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Cornwall Pothole Claims | 36,501 Potholes Filled in One Year | Fixtyer"
        description="Cornwall Council earns a GREEN condition score from the DfT — yet filled 36,501 potholes in 2024/25, watched RED-condition rural roads triple in a year, and cut its preventative spending share from 78% to 51%. See what the AMBER overall rating means for your pothole claim."
        canonical="/councils/cornwall"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Cornwall', url: '/councils/cornwall' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: "Cornwall's road condition score is GREEN — can I still claim?",
            answer: 'Yes. The GREEN condition score is a network-wide average dominated by well-maintained A roads. Look closer: unclassified rural roads in RED condition tripled from 2.4% to 7.3% in a single year, and the council filled 36,501 potholes in 2024/25 — around 100 every day. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on a county-wide average.',
          },
          {
            question: 'What if I hit a pothole on a rural lane or minor road?',
            answer: "That is where Cornwall's GREEN rating is weakest. Unclassified roads make up 3,579km — nearly half the 7,383km network — and 76% of the whole network is rural. The council's own survey coverage table shows minor rural access roads are machine-surveyed at only 50% coverage per year, and rural lanes at just 25%. The last published figure put 7.3% of unclassified roads in RED condition — roughly three times the level two years earlier.",
          },
          {
            question: 'Why is the Spend scorecard AMBER if the roads are rated GREEN?',
            answer: "Because the money is moving the wrong way. The share of spending classed as preventative collapsed from 78% in 2020/21 to 51% in 2024/25 — meaning nearly half of Cornwall's highways budget now goes on reactive patching rather than stopping potholes forming. Capital spend also came in below the DfT's notional allocation in 2020/21 (£28.6m against £38.5m) and 2023/24 (£33.6m against £36.9m). The council itself concludes it 'maintains its roads with lower expenditure' than comparable authorities.",
          },
          {
            question: 'Cornwall says 98-100% of pothole repairs are completed on time — does that defeat my claim?',
            answer: 'No. That KPI measures how quickly the contractor repairs defects after they have been logged. It says nothing about whether the pothole that damaged your car had been identified before your incident — which is the question Section 58 actually asks. The council also admits that where there is running or standing water it will "sometimes have no choice but to undertake temporary repairs", and that "occasional permanent repairs may also be more susceptible to failure". A failed or temporary repair that regenerated is strong claim evidence.',
          },
          {
            question: 'Does the council\'s "network in decline" admission help my claim?',
            answer: 'Yes. Cornwall\'s transparency report describes "the challenges of managing a network in decline", notes "an upward trend" in AMBER-condition roads, and warns that with a large proportion of the network in the Amber band "this switching to Red is possible". That is documented knowledge that deterioration is outpacing maintenance — which raises the bar for what a reasonable inspection regime looks like under Section 58.',
          },
          {
            question: 'Pothole repairs jumped almost 50% in two years — what does that mean for me?',
            answer: 'Potholes filled rose from 24,663 in 2022/23 to 36,501 in 2024/25 — an increase of almost half, and over 120,000 potholes in four years. The council links this directly to "a decline in overall condition". A network producing 100 new potholes a day is one where defects routinely form between inspections — exactly the scenario where prior reports and dated photographs decide claims.',
          },
        ]}
      />
      <ServiceSchema 
        councilName="Cornwall Council" 
        councilSlug="cornwall" 
        rating="amber" 
      />
      <SpeakableSchema
        url="/councils/cornwall"
        headline="Cornwall Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div 
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Cornwall</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Cornwall: GREEN Roads on Paper, 100 New Potholes a Day
          </h1>
          
          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Cornwall Council runs one of England's longest rural road networks — 7,383km, 76% of it 
            rural — and earns the DfT's only <strong className="text-emerald-400">GREEN condition score</strong> on 
            its scorecard. Yet the overall rating is <strong className="text-amber-400">AMBER</strong>: 
            preventative spending has collapsed from 78% to 51% of the budget, RED-condition rural roads 
            <strong className="text-rose-400"> tripled in a single year</strong>, and the council's own report 
            describes "a network in decline".
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">36,501</div>
            <div className="text-white font-medium mt-2">Potholes filled in 2024/25 alone</div>
            <div className="text-brand-300 text-sm mt-1">
              Around 100 potholes every single day — up almost 50% in two years, on a network 
              the DfT rates GREEN for condition.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            SCANNER survey data from Cornwall's own transparency report — excellent A roads, 
            steady B and C roads, and a rural network heading the other way
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (564km — 7.6% of network): genuinely good</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">1.3%</div>
                <div className="text-sm text-rose-800">RED (2023)</div>
                <div className="text-xs text-surface-500">held between 1.1% and 1.4% since 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">20.1%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">creeping up from 19.1%</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">78.6%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">down from 79.5% in 2020</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Credit where due: Cornwall's A roads are in better shape than most of England's. 
              But A roads are less than 8% of this network. The other 6,800km tell a different story.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (3,240km — 44% of network): steady, but 4 in 10 not GREEN</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">8%</td>
                    <td className="px-4 py-2 text-center">31.3%</td>
                    <td className="px-4 py-2 text-center">60.7%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">8%</td>
                    <td className="px-4 py-2 text-center">31.2%</td>
                    <td className="px-4 py-2 text-center">61.2%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">30.3%</td>
                    <td className="px-4 py-2 text-center">62.8%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">30.3%</td>
                    <td className="px-4 py-2 text-center">62.7%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>"TBC" — not published in the report</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              7% RED on 3,240km of B and C roads is roughly <strong>227km of road the council's own 
              survey says should be considered for maintenance</strong> — and another 30% sits in the 
              Amber "maintenance may be required soon" band. The council warns that with so much of 
              the network in Amber, "this switching to Red is possible".
            </p>
          </div>

          {/* U roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (3,579km — 48% of network): RED tripled in a year</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-900">U roads in RED condition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3.7%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">4.5%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2.4%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">7.3% — tripled in one year</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">"TBC" — not published in the report</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              The rural lanes and residential streets that make up nearly half of Cornwall's network 
              went from 2.4% RED to 7.3% RED between 2022 and 2023 — roughly 260km of unclassified 
              road in the worst condition band. This is the road class where the GREEN headline 
              rating is weakest, and where most drivers actually hit potholes.
            </p>
          </div>

          {/* The TBC problem */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">The Missing Year</h3>
            <p className="text-brand-200 mb-4">
              Cornwall's transparency report was published on 27 June 2025 — yet every condition 
              table shows <strong className="text-white">"TBC" for 2024</strong>. The most recent 
              published condition data is from 2023, before the wettest periods that the council 
              itself blames for the current pothole surge.
            </p>
            <p className="text-xl font-semibold text-white">
              The GREEN condition score rests on data that predates a winter the council says 
              drove pothole numbers to record levels. If your incident is recent, the published 
              condition picture may flatter the road you were driving on.
            </p>
          </div>
        </div>
      </section>

      {/* The Rural Survey Gap */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            One of England's Longest Rural Networks — Surveyed by Halves
          </h2>
          <p className="text-surface-600 mb-8">
            7,383km of road, 76% rural — and the council's own coverage table shows the minor 
            rural network is machine-surveyed at 50% coverage, with rural lanes at just 25%
          </p>

          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200 text-center">
              <div className="text-4xl font-bold text-brand-950">7,383km</div>
              <div className="text-surface-600 text-sm mt-2">
                Total road network — plus 231km of remote footways and nearly 4,500km of public rights of way
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200 text-center">
              <div className="text-4xl font-bold text-brand-950">76%</div>
              <div className="text-surface-600 text-sm mt-2">
                Rural, by the council's own maintenance hierarchy — 5,629km rural against 1,754km urban
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200 text-center">
              <div className="text-4xl font-bold text-brand-950">£14bn</div>
              <div className="text-surface-600 text-sm mt-2">
                Estimated replacement value of the highway asset — the council's most significant physical asset
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Road type (council's survey coverage table)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Machine survey coverage</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">A roads (SCANNER)</td>
                  <td className="px-6 py-4 text-center">50% two-way per year</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">B and C roads — main and secondary distributors (SCANNER)</td>
                  <td className="px-6 py-4 text-center">100% one-way per year</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 text-brand-900 font-medium">C roads — local and minor access roads (SCANNER)</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-700">50% one-way per year</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 text-brand-900 font-medium">Unclassified local and minor access roads (Multifunctional Road Monitor)</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-700">50% per year</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 text-brand-900 font-bold">Rural lanes (Multifunctional Road Monitor)</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">25% per year</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What 50% and 25% Coverage Means</h3>
              <p className="text-surface-600 text-sm mb-3">
                On the minor rural network — the roads where the RED figure tripled — a given stretch 
                may only pass under a condition survey machine <strong>once every two years</strong>, 
                and on rural lanes potentially <strong>once every four</strong>.
              </p>
              <p className="text-surface-600 text-sm">
                Between machine surveys, the council relies on routine safety inspections and public 
                reports. On a 7,383km network producing 100 potholes a day, that is a lot of road 
                deteriorating between official measurements.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Hierarchy Question</h3>
              <p className="text-amber-800 text-sm mb-3">
                Cornwall prioritises by a "maintenance hierarchy and resilient network" — usage and 
                importance rather than traditional road classifications. Sensible asset management, 
                but it concentrates attention on strategic routes.
              </p>
              <p className="text-amber-800 text-sm">
                If you hit a pothole on a 4b local access road or a 5a minor access road — 
                categories covering over 3,400km of rural Cornwall — ask when that specific road was 
                last surveyed and inspected. The answer is often the weakest link in a Section 58 defence.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under Section 41 of the Highways Act 1980, Cornwall Council has a legal duty to maintain 
              its roads — its own report says so in terms. To escape liability under Section 58, it must 
              show its inspection and repair system was reasonable <em>for the road you were on</em>. Ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last machine-surveyed — and was it in the uncovered half (or three-quarters) that year?</li>
              <li>• If 7.3% of unclassified roads were RED at the last published survey, what was done about yours?</li>
              <li>• How did the inspection regime respond when pothole numbers rose almost 50% in two years?</li>
              <li>• Was the defect a regenerated temporary repair — the failure mode the council itself flags on wet roads?</li>
            </ul>
            <p className="text-white font-medium">
              A GREEN network average is not a defence. The defence has to hold on one road, on one day, 
              for one defect — and that is where the rural survey gap bites.
            </p>
          </div>
        </div>
      </section>

      {/* The Money */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Why The Spend Scorecard Is AMBER
          </h2>
          <p className="text-surface-600 mb-8">
            The preventative share of spending has collapsed — and capital spend has twice come in 
            below the DfT's notional allocation
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">DfT capital allocation</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Capital spend</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Preventative share</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Reactive share</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">£38.5m</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">£28.6m</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">78%</td>
                  <td className="px-6 py-4 text-center">24%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">£28.3m</td>
                  <td className="px-6 py-4 text-center">£32.0m</td>
                  <td className="px-6 py-4 text-center">74%</td>
                  <td className="px-6 py-4 text-center">26%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">£28.3m</td>
                  <td className="px-6 py-4 text-center">£35.2m</td>
                  <td className="px-6 py-4 text-center">63%</td>
                  <td className="px-6 py-4 text-center">37%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">£36.9m</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">£33.6m</td>
                  <td className="px-6 py-4 text-center">66%</td>
                  <td className="px-6 py-4 text-center">34%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">£31.9m</td>
                  <td className="px-6 py-4 text-center">£39.6m</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">51%</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">49%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono text-brand-900">2025/26</td>
                  <td className="px-6 py-4 text-center">£44.3m</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">TBC</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">TBC</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">TBC</td>
                </tr>
              </tbody>
            </table>
            <p className="text-xs text-surface-500 px-6 py-3 border-t border-surface-100">
              Figures as published in Cornwall Council's transparency report. Preventative/reactive splits are the 
              council's own estimates as printed (2020/21 figures appear in the report as 78% and 24%). 
              Cornwall sits in the 100% business rates retention pilot, so DfT allocations are notional 
              amounts within its funding settlement rather than direct grants.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Preventative Collapse: 78% → 51%</h3>
              <p className="text-amber-800 text-sm mb-3">
                In 2020/21, 78p in every maintenance pound went on preventative work — surfacing roads 
                before they fail. By 2024/25 that was down to 51p, with nearly half the budget consumed 
                by reactive patching.
              </p>
              <p className="text-amber-800 text-sm">
                Every highways engineer knows where that ends: reactive spend is roughly ten times less 
                efficient than prevention. A budget being eaten by potholes is a leading indicator that 
                today's GREEN condition score is living on borrowed time.
              </p>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">"Lower Expenditure" — The Council's Own Conclusion</h3>
              <p className="text-surface-600 text-sm mb-3">
                Benchmarking against its APSE family group, the report states plainly: "The conclusion 
                is that Cornwall maintains its roads with lower expenditure."
              </p>
              <p className="text-surface-600 text-sm">
                Efficient — or under-resourced? On a £14 billion asset, total annual maintenance spend 
                has run at 0.29%–0.42% of asset value. Traffic on this network has nearly doubled since 
                2000, from 2.5 billion to 4.8 billion vehicle miles a year.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Pothole Surge */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            120,124 Potholes in Four Years — and Accelerating
          </h2>
          <p className="text-surface-600 mb-8">
            The council's own repair figures, "noting a decline in overall condition"
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Financial year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Per day (approx.)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">24,492</td>
                  <td className="px-6 py-4 text-center">67</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">24,663</td>
                  <td className="px-6 py-4 text-center">68</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">34,468</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">94</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">36,501</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">100</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2025/26 (to 8 May)</td>
                  <td className="px-6 py-4 text-center">3,552</td>
                  <td className="px-6 py-4 text-center">~93</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Four-year total (2021/22–2024/25)</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">120,124</td>
                  <td className="px-6 py-4 text-center font-bold">82</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Up Almost 50% in Two Years</h3>
              <p className="text-surface-600 text-sm">
                From 24,663 potholes in 2022/23 to 36,501 in 2024/25 — a 48% increase. The council 
                expects "a slight increase this year" before its new repair innovations "begin to hold 
                this steady state". That is the council's own forecast: more potholes again in 2025/26 
                before any improvement.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Temporary Repair Admission</h3>
              <p className="text-amber-800 text-sm">
                Where there is running or standing water, the council says it will "sometimes have 
                no choice but to undertake temporary repairs and programme in permanent repairs when 
                conditions permit" — and that "occasional permanent repairs may also be more 
                susceptible to failure". In a county with Cornwall's rainfall, a pothole that came 
                back after a patch is a documented, foreseeable failure mode.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">100 Potholes a Day Is Not a GREEN-Condition Statistic</h3>
            <p className="text-brand-200 mb-4">
              A network producing potholes at this rate is, by definition, one where defects routinely 
              form between inspections. The council's stated KPI — 98-100% of pothole repairs completed 
              on time — only starts the clock <em>once a defect is logged</em>.
            </p>
            <p className="text-white font-medium">
              The decisive question for your claim is what happened before the clock started: had the 
              pothole been reported, had it been inspected, and had a previous repair already failed. 
              Prior reports and dated photographs answer all three.
            </p>
          </div>
        </div>
      </section>

      {/* In Their Own Words */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            "A Network in Decline" — In The Council's Own Words
          </h2>
          <p className="text-surface-600 mb-8">
            Verbatim admissions from the 2025/26 transparency report, signed off by the council's 
            Portfolio Holder for Transport in June 2025
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="Despite the challenges of managing a network in decline, we focus on proactive strategies such as regular inspections, data-driven planning, and community engagement to maintain and improve our highways."
              source="Cornwall Council Local Highways Maintenance Transparency Report 2025/26"
            />
            <QuoteCard 
              quote="There has however been an upward trend the Amber bands and whilst our road network is in better condition than some of our peers at the end of 2021 it was still categorised as, average, despite the opinion of some that it is in excellent condition. Increasing short duration high intensity rainfall events have exacerbated this decline and with a large proportion of our network in the Amber band, this switching to Red is possible."
              source="Cornwall Council Local Highways Maintenance Transparency Report 2025/26 (as published)"
            />
            <QuoteCard 
              quote="The current increase in pothole numbers is largely due unusually wet weather over an extended period of several months and the impact this is having on road surfaces."
              source="Cornwall Council Local Highways Maintenance Transparency Report 2025/26 (as published)"
            />
            <QuoteCard 
              quote="You can see and increase in numbers noting a decline in overall condition. It is likely that we will see a slight increase this year but with the new innovations we hope to begin to hold this steady state."
              source="Cornwall Council Local Highways Maintenance Transparency Report 2025/26 (as published)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What These Admissions Mean</h3>
              <p className="text-surface-600 text-sm mb-3">
                Cornwall Council formally acknowledges — in a published, signed document — that its 
                network is in decline, that intense rainfall is accelerating deterioration, and that 
                rising pothole numbers reflect "a decline in overall condition".
              </p>
              <p className="text-surface-600 text-sm">
                Documented knowledge of accelerating deterioration raises the bar for what a 
                "reasonable" inspection and repair regime looks like under Section 58 — particularly 
                on the rural roads surveyed at 50% or 25% coverage.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• If the network is admittedly declining, were inspection frequencies increased to match?</li>
                <li>• The council knew wet weather was driving a pothole surge — what changed on drainage-prone roads?</li>
                <li>• Was the defect that damaged your vehicle a temporary repair that failed, as the report says can happen?</li>
                <li>• Why does a report published in June 2025 contain no condition data newer than 2023?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Claiming against a GREEN-condition council */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN-Condition Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Cornwall's roads compare well nationally — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> The DfT's GREEN condition score — A roads at just 1.3% RED
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> NHT survey ranks Cornwall's road condition in the top quartile nationally
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> APSE benchmarking: first in its family group for quality of repair
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 98-100% of logged pothole repairs completed on time
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented risk-based regime: SCANNER, SCRIM, pothole-cluster targeting, a published Highway Maintenance Manual
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a competent, well-documented Section 58 defence — especially on A roads. 
                A generic "your roads are terrible" claim will not work in Cornwall.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED-condition unclassified roads tripled in a year — 2.4% to 7.3%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Minor rural roads machine-surveyed at 50% coverage; rural lanes at 25%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 36,501 potholes filled in 2024/25 — up 48% in two years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Preventative spending share collapsed from 78% to 51%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER spend and AMBER best practice scorecards from the DfT
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Written admissions: "a network in decline", weather-driven deterioration, temporary repairs that fail
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> No published condition data for 2024 — the GREEN score rests on pre-surge surveys
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council that can wave a GREEN condition score at a judge, your claim lives or 
              dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — via the council's own Report It system, FixMyStreet or community Facebook groups — are proof of actual notice</li>
              <li>• Photos showing size, depth and visible age: weathered edges, standing water, the remains of a previous patch</li>
              <li>• The road's place in the maintenance hierarchy — on a 4b, 5a or 5b rural road, the 50%/25% survey coverage is your strongest structural argument</li>
              <li>• Timing: an incident after autumn 2023 post-dates every condition figure the council has published</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, 
              and cites Cornwall's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Cornwall?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A GREEN-rated council demands a sharper claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No rural survey-coverage argument</li>
                <li>• No prior-report search</li>
                <li>• No answer to the GREEN condition score</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U-road RED tripling documented</li>
                <li>✅ 50%/25% rural survey coverage argued</li>
                <li>✅ 36,501 potholes in one year cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Cornwall</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=cornwall"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Cornwall Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Cornwall's road condition score is GREEN — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The GREEN condition score is a network-wide average dominated by well-maintained 
                A roads. Look closer: unclassified rural roads in RED condition tripled from 2.4% to 
                7.3% in a single year, and the council filled 36,501 potholes in 2024/25 — around 100 
                every day. Section 58 turns on whether the specific defect that damaged your vehicle 
                was reasonably inspected and repaired, not on a county-wide average.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if I hit a pothole on a rural lane or minor road?
              </h3>
              <p className="text-surface-600 text-sm">
                That is where Cornwall's GREEN rating is weakest. Unclassified roads make up 3,579km — 
                nearly half the 7,383km network — and 76% of the whole network is rural. The council's 
                own survey coverage table shows minor rural access roads are machine-surveyed at only 
                50% coverage per year, and rural lanes at just 25%. The last published figure put 7.3% 
                of unclassified roads in RED condition — roughly three times the level two years earlier.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is the Spend scorecard AMBER if the roads are rated GREEN?
              </h3>
              <p className="text-surface-600 text-sm">
                Because the money is moving the wrong way. The share of spending classed as preventative 
                collapsed from 78% in 2020/21 to 51% in 2024/25 — meaning nearly half of Cornwall's 
                highways budget now goes on reactive patching rather than stopping potholes forming. 
                Capital spend also came in below the DfT's notional allocation in 2020/21 (£28.6m against 
                £38.5m) and 2023/24 (£33.6m against £36.9m). The council itself concludes it "maintains 
                its roads with lower expenditure" than comparable authorities.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Cornwall says 98-100% of pothole repairs are completed on time — does that defeat my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No. That KPI measures how quickly the contractor repairs defects after they have been 
                logged. It says nothing about whether the pothole that damaged your car had been 
                identified before your incident — which is the question Section 58 actually asks. The 
                council also admits that where there is running or standing water it will "sometimes 
                have no choice but to undertake temporary repairs", and that "occasional permanent 
                repairs may also be more susceptible to failure". A failed or temporary repair that 
                regenerated is strong claim evidence.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the council's "network in decline" admission help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Cornwall's transparency report describes "the challenges of managing a network in 
                decline", notes "an upward trend" in AMBER-condition roads, and warns that with a large 
                proportion of the network in the Amber band "this switching to Red is possible". That is 
                documented knowledge that deterioration is outpacing maintenance — which raises the bar 
                for what a reasonable inspection regime looks like under Section 58.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs jumped almost 50% in two years — what does that mean for me?
              </h3>
              <p className="text-surface-600 text-sm">
                Potholes filled rose from 24,663 in 2022/23 to 36,501 in 2024/25 — an increase of almost 
                half, and over 120,000 potholes in four years. The council links this directly to "a 
                decline in overall condition". A network producing 100 new potholes a day is one where 
                defects routinely form between inspections — exactly the scenario where prior reports 
                and dated photographs decide claims.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a 
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a 
              href="https://www.cornwall.gov.uk/transport-parking-and-streets/roads-highways-and-pavements/local-highways-maintenance-transparency-report-202526-summary/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Cornwall Council Local Highways Maintenance Transparency Report 2025/26 (published 27 June 2025)
            </a>. 
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
